import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSoftphone } from "../context/SoftphoneContext";
import { supabase } from "../lib/supabase";
import { formatPhone, toE164 } from "../lib/phoneFormat";
import { invokeErrorMessage } from "../lib/edgeError";
import { SOFTPHONE_RECONNECT_HINT } from "./DealCallText";

interface Props {
  dealId: string;
  homeownerName?: string | null;
  homeownerPhone?: string | null;
  /** Extra Tailwind classes on the outer button. */
  className?: string;
}

/**
 * Portal button that initiates an outbound call to a homeowner via the
 * twilio-start-call edge function. The call bridges through the Bardin Twilio
 * number (470) 823-8816 so the homeowner sees the company number on caller ID.
 *
 * Flow: button click → lookup caller's cell from team_roster → invoke
 * twilio-start-call → Twilio dials the RM/staff, then bridges to homeowner.
 */
export default function CallHomeownerButton({
  dealId,
  homeownerName,
  homeownerPhone,
  className = "",
}: Props) {
  const { member } = useAuth();
  const softphone = useSoftphone();
  const [busy, setBusy] = useState(false);

  if (!homeownerPhone) return null;

  const softphoneLive = softphone.isEnabled && (softphone.status === "ready" || softphone.status === "registering");
  // Cell bridge only rings US numbers — softphone-only reps (international /
  // missing cell, e.g. Hennie) get an on-demand softphone revival instead.
  // Mirrors DealCallText.handleCall; keep the two in sync.
  const bridgeable = !!toE164(member?.phone);
  const useSoftphonePath = softphoneLive || (softphone.isEnabled && !bridgeable);

  async function handleCall() {
    if (!homeownerPhone) return;
    const promptMsg = useSoftphonePath
      ? `Call ${homeownerName ?? "homeowner"} at ${formatPhone(homeownerPhone)}?\n\nThe call will ring through your laptop softphone.`
      : `Call ${homeownerName ?? "homeowner"} at ${formatPhone(homeownerPhone)}?\n\nYour phone will ring first, then we'll connect you.`;
    const ok = window.confirm(promptMsg);
    if (!ok) return;

    setBusy(true);
    try {
      if (useSoftphonePath) {
        try {
          await softphone.call({
            phoneNumber: homeownerPhone,
            dealId,
            displayName: homeownerName ?? null,
          });
        } catch (err) {
          if (!softphoneLive) throw new Error(SOFTPHONE_RECONNECT_HINT);
          throw err;
        }
      } else {
        const { data, error } = await supabase.functions.invoke("twilio-start-call", {
          body: { deal_id: dealId, rm_id: member?.id ?? null },
        });
        if (error) throw new Error(await invokeErrorMessage(error, "Call failed"));
        if (!data?.ok) throw new Error(data?.error || "Call failed");
      }
    } catch (e) {
      alert("Couldn't start call: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={handleCall}
      disabled={busy}
      className={
        "px-3 py-1.5 rounded bg-emerald-100 text-emerald-800 text-sm hover:bg-emerald-200 disabled:opacity-50 " +
        className
      }
      title={`Call ${homeownerName ?? "homeowner"}`}
    >
      {busy ? "Dialing…" : "📞 Call"}
    </button>
  );
}
